import { ComponentInstance } from "@uniformdev/canvas";
import { type ComponentProps, UniformSlot } from "@uniformdev/canvas-react";

import ProductTemplateContextProvider from "@/components/product-template-context-provider";
import { IntercomProvider } from "@/libraries/intercom/src";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import GlobalNav from "@/patterns/global-nav/src";
import { StrategyCollectionNavbar } from "@/templates/collections/strategy/pricing/sections/strategy-collection-navbar.tsx";
import { ServiceCollectionNavbar } from "@/templates/collections/teamwork/pricing/sections/service/service-collection-navbar.tsx";
import { TeamworkCollectionNavbar } from "@/templates/collections/teamwork/pricing/sections/teamwork-collection-navbar.tsx";
import EnterprisePageProductNav from "@/templates/enterprise/enterprise-landing-page/sections/enterprise-nav";
import { BitbucketNav } from "@/templates/software/bitbucket/src/sections/bitbucket-navbar.tsx";
import { CompassNav } from "@/templates/software/compass/src/sections/compass-navbar.tsx";
import ConfluenceSoftwareNav from "@/templates/software/confluence/confluence-software-page/src/sections/confluence-navbar";
import { ConfluenceTemplateHeaderContainer } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/shared-header-footer-container.tsx";
import { GuardNav } from "@/templates/software/guard/src/sections/guard-navbar.tsx";
import JiraProductDiscoveryNav from "@/templates/software/jira/product-discovery/jira-product-discovery-software-page/src/sections/product-nav.tsx";
import JiraSoftwareNav from "@/templates/software/jira/sections/nav-bar/software-jira-navbar";
import JiraSoftwareNavUniform from "@/templates/software/jira/sections/nav-bar/software-jira-navbar-uniform";
import JiraServiceManagementSoftwareNavV2 from "@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2";
import JiraServiceManagementSoftwareNavV2Uniform from "@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2-uniform";
import { LoomNav } from "@/templates/software/loom/pricing/sections/loom-navbar.tsx";
import { RovoNav } from "@/templates/software/rovo/rovo-nav.tsx";
import { SourcetreeAppFooter } from "@/templates/sourcetreeapp/sourcetree-app-footer.tsx";
import { SourcetreeAppNav } from "@/templates/sourcetreeapp/sourcetree-app-nav.tsx";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useLocale } from "../../../../libraries/web-framework/src/localization/use-locale";

import { RovoDevNav } from "./navbars/rovo-dev-navbar.tsx";
import { OptionalSiteSelectorContextProvider } from "./optional-contexts.tsx";

export interface PageProps extends ComponentProps {
    pageTitle: string;
    metaDescription?: string;
    siteSelectorProductKey?: string;
    siteSelectorAnalyticsPrefix?: string;
}

export const Page = (props: PageProps) => {
    const { component, siteSelectorProductKey, siteSelectorAnalyticsPrefix } =
        props;

    const fullSiteSelectorProductKey =
        siteSelectorProductKey === "jira-service-management"
            ? "jira-servicedesk.ondemand"
            : `${siteSelectorProductKey}.ondemand`;

    const locale = useLocale();

    const isBootIntercom: boolean = checkFeatureFlagGate(
        featureFlags.gwp5381AddIntercomWidgetToUniform
    );

    const intercomAppId = locale === "ja" ? "p0ww63i2" : "";

    const contentSlot = component.slots?.["content"] ?? [];

    const pageHasGlobalNavComponent = !!(
        contentSlot as ComponentInstance[]
    ).find((content) => content.type === "globalNav");
    const pageHasGlobalFooterComponent = !!(
        contentSlot as ComponentInstance[]
    ).find((content) => content.type === "globalFooter");

    return (
        <IntercomProvider appId={intercomAppId} triggerBoot={isBootIntercom}>
            <ProductTemplateContextProvider>
                <OptionalSiteSelectorContextProvider
                    siteSelectorProductKey={fullSiteSelectorProductKey}
                    siteSelectorAnalyticsPrefix={siteSelectorAnalyticsPrefix}
                >
                    <TypographyStyles>
                        <UniformSlot name="enhancedBannerSlot" />
                    </TypographyStyles>
                    {!pageHasGlobalNavComponent && (
                        <PageNav component={component} />
                    )}
                    <TypographyStyles>
                        <main id="content">
                            <UniformSlot name="content" />
                        </main>
                    </TypographyStyles>
                </OptionalSiteSelectorContextProvider>
            </ProductTemplateContextProvider>
            {!pageHasGlobalFooterComponent && (
                <PageFooter component={component} />
            )}
        </IntercomProvider>
    );
};

// eslint-disable-next-line complexity
export const PageNav = ({ component }: ComponentProps) => {
    const pageNavType =
        component.parameters &&
        component.parameters["nav"] &&
        component.parameters["nav"].value;
    let pageNav;

    switch (pageNavType) {
        case "confluence":
            pageNav = <ConfluenceSoftwareNav />;
            break;
        case "bitbucket":
            pageNav = <BitbucketNav />;
            break;
        case "confluence-template-experiment":
            pageNav = <ConfluenceTemplateHeaderContainer />;
            break;
        case "enterprise":
            pageNav = <EnterprisePageProductNav />;
            break;
        case "jira-software":
            pageNav = <JiraSoftwareNav />;
            break;
        case "jira-service-management":
            pageNav = <JiraServiceManagementSoftwareNavV2 />;
            break;
        case "jira-product-discovery":
            pageNav = <JiraProductDiscoveryNav />;
            break;
        case "gwp-7880-jira-software-templates-experiment":
            pageNav = <JiraSoftwareNavUniform />;
            break;
        case "gwp-7880-jira-service-management-templates-experiment":
            pageNav = <JiraServiceManagementSoftwareNavV2Uniform />;
            break;
        case "compass":
            pageNav = <CompassNav />;
            break;
        case "atlassian-access":
        case "guard":
            pageNav = <GuardNav />;
            break;
        case "rovo":
            pageNav = <RovoNav />;
            break;
        case "rovo-dev":
            pageNav = <RovoDevNav />;
            break;
        case "loom":
            pageNav = <LoomNav />;
            break;
        case "teamwork-collection":
            pageNav = <TeamworkCollectionNavbar />;
            break;
        case "strategy-collection":
            pageNav = <StrategyCollectionNavbar />;
            break;
        case "service-collection":
            pageNav = <ServiceCollectionNavbar />;
            break;
        case "sourcetree-app":
            pageNav = <SourcetreeAppNav />;
            break;
        case "none":
            break;
        case "global":
        default:
            pageNav = (
                <GlobalNav productKey="uniform" isShowTryNowLink={true} />
            );
            break;
    }

    return pageNav;
};

export const PageFooter = ({ component }: ComponentProps) => {
    const pageFooterType =
        component.parameters &&
        component.parameters["footer"] &&
        component.parameters["footer"].value;
    let pageFooter;
    switch (pageFooterType) {
        case "sourcetree-app":
            pageFooter = <SourcetreeAppFooter />;
            break;
        case "none":
            break;
        case "global":
        default:
            pageFooter = <GlobalFooter />;
            break;
    }

    return pageFooter;
};

export default Page;
